import { useState, useEffect, useCallback } from "react";
import LoadingInComponent from "../../Components/LoadingInComponent";
import Breadcrumb from "../../Components/Breadcrumb";
import Button from "../../Components/Button";
import Can from "../../Components/Can";
import { Add } from "iconsax-react";
import UserListTable from "../../Components/Tables/UserListTable";
import RoleListTable from "../../Components/Tables/RoleListTable";
import RoleModal from "../../Components/RoleModal";
import {
  GET_USER_LIST_API,
  GET_ROLE_LIST_API,
  GET_ROLE_DETAILS_API,
} from "../../Constant/api";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { USER_CREATE } from "../../Router/path";
import Header from "../../Components/Header";
import { useToast } from "../../Components/Toast";

const UserList = () => {
  const breadcrumbItems = [
    { label: "User Management", path: null },
    { label: "List", path: null },
  ];
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tableData, setTableData] = useState(null);
  const [tableMeta, setTableMeta] = useState(null);
  const [roleTableData, setRoleTableData] = useState(null);
  const [roleTableMeta, setRoleTableMeta] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("users");
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const fetchUserList = useCallback(
    async (page = 1, perPageValue = perPage, search = searchTerm) => {
      setLoading(true);
      try {
        const res = await axios.get(GET_USER_LIST_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: {
            search: search,
            page: page,
            perPage: perPageValue,
          },
        });
        setLoading(false);
        setTableData(res.data.data);
        setTableMeta(res.data.meta.pagination);
      } catch (err) {
        setLoading(false);
        console.error(err);
        toast("Failed to fetch user list.", "error");
      }
    },
    [perPage, searchTerm, toast],
  );

  const fetchRoleList = useCallback(
    async (page = 1, perPageValue = perPage, search = searchTerm) => {
      setLoading(true);
      try {
        const res = await axios.get(GET_ROLE_LIST_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: {
            search: search,
            page: page,
            perPage: perPageValue,
          },
        });
        setLoading(false);
        setRoleTableData(res.data.data);
        setRoleTableMeta(res.data.meta.pagination);
      } catch (err) {
        setLoading(false);
        console.error(err);
        toast("Failed to fetch role list.", "error");
      }
    },
    [perPage, searchTerm, toast],
  );

  const handleEditRole = async (roleId) => {
    try {
      setLoading(true);
      const res = await axios.get(`${GET_ROLE_DETAILS_API}/${roleId}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      });

      const roleData = {
        id: res.data.data.role_info.id,
        name: res.data.data.role_info.name,
        permissions: res.data.data.permissions,
      };

      setEditingRole(roleData);
      setShowRoleModal(true);
    } catch (err) {
      console.error(err);
      toast("Failed to fetch role details.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(e.target.value);
    if (activeTab === "users") {
      fetchUserList(1, e.target.value, searchTerm);
    } else {
      fetchRoleList(1, e.target.value, searchTerm);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (activeTab === "users") {
        fetchUserList(1, perPage, searchTerm);
      } else {
        fetchRoleList(1, perPage, searchTerm);
      }
    }
  };

  const handleClearFilter = () => {
    setSearchTerm("");
    if (activeTab === "users") {
      fetchUserList(1, perPage, "");
    } else {
      fetchRoleList(1, perPage, "");
    }
  };

  useEffect(() => {
    setSearchTerm("");

    if (activeTab === "users") {
      fetchUserList(1, perPage, "");
    } else {
      fetchRoleList(1, perPage, "");
    }
  }, [activeTab, fetchUserList, fetchRoleList, perPage]);

  const handleSaveRole = () => {
    setShowRoleModal(false);
    setEditingRole(null);
    fetchRoleList(1);
  };

  const handleCloseModal = () => {
    setShowRoleModal(false);
    setEditingRole(null);
  };

  return (
    <div className="flex flex-col w-full h-full">
      <Header noSidebar={true} />

      <LoadingInComponent isLoading={loading}>
        <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[12px] md:gap-[24px]">
          <Breadcrumb items={breadcrumbItems} />

          <div className="flex flex-col md:flex-row w-full justify-between items-center">
            <h1 className="text-[29px]">User Management</h1>
            <div className="flex w-fit items-center gap-[8px]">
              <Can
                permission={
                  activeTab === "users" ? "create_user" : "create_role"
                }
              >
                <Button
                  variant="primary"
                  onClick={() => {
                    if (activeTab === "users") {
                      navigate(USER_CREATE);
                    } else {
                      setEditingRole(null);
                      setShowRoleModal(true);
                    }
                  }}
                >
                  <Add size={16} variant="Outline" />
                  {activeTab === "users"
                    ? "Create New User"
                    : "Create New Role"}
                </Button>
              </Can>
            </div>
          </div>

          <div className="flex w-full border-b border-gray-200">
            <button
              onClick={() => setActiveTab("users")}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "users"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              User Management
            </button>
            <Can permission="view_role">
              <button
                onClick={() => setActiveTab("roles")}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === "roles"
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Role Management
              </button>
            </Can>
          </div>

          {activeTab === "users" && tableData && (
            <UserListTable
              searchTerm={searchTerm}
              handleSearch={handleSearch}
              handleKeyDown={handleKeyDown}
              data={tableData}
              meta={tableMeta}
              clearFilter={handleClearFilter}
              perPage={perPage}
              handlePerPageChange={handlePerPageChange}
              handlePageClick={(selectedItem) =>
                fetchUserList(selectedItem.selected + 1)
              }
            />
          )}

          {activeTab === "roles" && roleTableData && (
            <RoleListTable
              searchTerm={searchTerm}
              handleSearch={handleSearch}
              handleKeyDown={handleKeyDown}
              data={roleTableData}
              meta={roleTableMeta}
              clearFilter={handleClearFilter}
              perPage={perPage}
              handlePerPageChange={handlePerPageChange}
              handlePageClick={(selectedItem) =>
                fetchRoleList(selectedItem.selected + 1)
              }
              onRefresh={() => fetchRoleList(1)}
              onEdit={handleEditRole}
            />
          )}
        </div>
      </LoadingInComponent>

      {showRoleModal && (
        <RoleModal
          toggleModal={handleCloseModal}
          onCreateRole={handleSaveRole}
          roleData={editingRole}
        />
      )}
    </div>
  );
};

export default UserList;
